import { Show, UserProfile, useOrganization, useUser } from "@clerk/react";
import {
  Button,
  LogoBlueOnWhite,
  PrimaryFullScreenSpinner,
  Txt,
} from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { RiShieldUserLine } from "react-icons/ri";
import logoBlueOnWhite from "~/assets/brand/logoBlueOnWhite.svg";
import { RedirectToSignInWithDelay } from "~/components/RedirectToSignInWithDelay";
import { opsHead } from "~/head";

export const Route = createFileRoute("/user-profile/")({
  head: () => opsHead("Profile"),
  component: UserProfileRoute,
});

export function UserProfileRoute() {
  const user = useUser();
  const { organization } = useOrganization();

  if (!user.isLoaded) {
    return <PrimaryFullScreenSpinner />;
  }

  const mfaRequired =
    !!organization?.publicMetadata?.["user_mfa_required"] &&
    user.user?.twoFactorEnabled !== true;

  return (
    <>
      <Show when="signed-in">
        <Suspense fallback={<PrimaryFullScreenSpinner />}>
          <div className="grid min-h-dvh grid-rows-[5rem_1fr_5rem] gap-4 bg-unauthed-shell">
            <div className="place-self-start justify-self-start px-6 py-4">
              <LogoBlueOnWhite src={logoBlueOnWhite} />
            </div>
            <div className="grid place-content-center place-items-center gap-8">
              {mfaRequired && (
                <div className="mx-4 grid max-w-3xl justify-center justify-items-center gap-6 rounded-xl border border-warning bg-warning-muted p-4 py-6 md:px-12">
                  <div className="grid gap-3">
                    <div className="grid gap-1">
                      <div className="text-center text-warning">
                        <Txt variant="hero-title" tone="inherit">
                          <RiShieldUserLine className="me-1 inline align-middle text-[1.3em]" />
                          <span className="align-middle">
                            Action required: enable two-step verification
                          </span>
                        </Txt>
                      </div>
                    </div>

                    <div className="text-center">
                      <Txt variant="body">
                        Please click{" "}
                        <span className="whitespace-nowrap rounded-md border bg-primary px-1 py-0.5">
                          + Add two-step verification
                        </span>{" "}
                        in the Security panel below, and follow the prompts.
                      </Txt>
                    </div>
                  </div>
                  <div className="grid">
                    <div className="text-center">
                      <Txt variant="description">
                        Primary needs your help to keep your information secure.
                        This extra step helps prevent unauthorized access, even
                        if someone knows your password. <br />
                      </Txt>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-self-end"
                      asChild
                    >
                      <a href="https://clerk.chat/blog/how-2fa-enhances-security/">
                        What is two-step verification?
                      </a>
                    </Button>
                  </div>
                </div>
              )}
              <div className="min-h-[700px]">
                <UserProfile />
              </div>
            </div>
          </div>
        </Suspense>
      </Show>
      <Show when="signed-out">
        <RedirectToSignInWithDelay />
      </Show>
    </>
  );
}
